import { computed, inject, Injectable, signal } from '@angular/core';
import { DayEnum, INotification, INotificationRequest, NotificationEnum } from './model/notifications.model';
import { NotificationsCrudService } from './service/notifications-crud.service';
import { FriendsCrudService } from 'app/friends/service/friends-crud.service';
import { combineLatest, map, mergeMap, of } from 'rxjs';
import { StoryService } from 'app/my-history/story/story.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private crudService = inject(NotificationsCrudService);
  private friendsCrudService = inject(FriendsCrudService);
  private storyService = inject(StoryService);
  private _notifications = signal<INotification[]>([]);
  total = signal(0);
  reviewTotal = signal(0);
  activityTotal = signal(0);
  selectedTab = signal(NotificationEnum.ACTIVITY);
  notifications = computed(() => this.groupByDate(this._notifications()));
  notificationLength = computed(() => this._notifications().length);
  isLoading = signal(false);
  isLast = signal(false);
  dialogOpened = signal(false);
  preHandled = signal<string[]>([]);
  preRead = signal<string[]>([]);
  isEdit = signal(false);
  selectedIds = signal<string[]>([]);

  toggleEdit(state?: boolean) {
    this.isEdit.set(state ?? !this.isEdit());
  }

  toggleId(id: string) {
    if (this.selectedIds().includes(id)) {
      this.selectedIds.set(this.selectedIds().filter((selected) => selected !== id));
    } else {
      this.selectedIds.set([...this.selectedIds(), id]);
    }
  }

  resetEdit() {
    this.toggleEdit(false);
    this.selectedIds.set([]);
  }

  getUnread() {
    this.crudService.getUnreadNotifications().subscribe({
      next: ({ total, activity, handleable }) => {
        this.total.set(total);
        this.reviewTotal.set(handleable);
        this.activityTotal.set(activity);
      },
    });
  }

  getNotifications(request: INotificationRequest) {
    this.isLoading.set(true);
    if (!request.skip) {
      // reset state
      this._notifications.set([]);
      this.isLast.set(false);
    }
    if (request.handleable) {
      request.handled = false;
    }

    this.crudService.getNotifications(request).subscribe({
      next: (notifications) => {
        if (notifications.total) {
          this._notifications.set([...this._notifications(), ...notifications.data]);
        }
        this.isLoading.set(false);
        if (this._notifications().length >= notifications.total) {
          this.isLast.set(true);
        }
        if (this.dialogOpened()) {
          this.getUnread();
        }
      },
      error: (error) => {
        console.error('Error loading facets:', error);
        this._notifications.set([]);
      },
    });
  }

  toggleDialog(state?: boolean) {
    const opened = state ?? !this.dialogOpened();
    this.dialogOpened.set(opened);
    if (!opened) {
      this.resetEdit();
      this.selectedTab.set(NotificationEnum.ACTIVITY);
    }
  }

  markAsRead(notification: INotification) {
    this.crudService.markAsRead([notification.id]).subscribe(() => {
      this.preRead.set([...this.preRead(), notification.id]);
      this.getUnread();
    });
  }

  shareTo(followerId: number, notificationId: string) {
    this.friendsCrudService
      .shareTo(`${followerId}`)
      .pipe(mergeMap(() => this.crudService.markAsRead([notificationId])))
      .subscribe(() => {
        this.preRead.set([...this.preRead(), notificationId]);
        this.preHandled.set([...this.preHandled(), notificationId]);
      });
  }

  delete(followerId: number, notificationId: string) {
    this.friendsCrudService
      .removeFromFriends(`${followerId}`)
      .pipe(mergeMap(() => this.crudService.markAsRead([notificationId])))
      .subscribe(() => {
        this.preRead.set([...this.preRead(), notificationId]);
        this.preHandled.set([...this.preHandled(), notificationId]);
      });
  }

  approve(commentId: string, notificationId: string) {
    this.storyService
      .approveComment(`${commentId}`)
      .pipe(
        mergeMap(() => this.crudService.markAsHandled(notificationId)),
        mergeMap(() => this.crudService.markAsRead([notificationId])),
      )
      .subscribe(() => {
        this._notifications.set(this._notifications().filter((item) => item.id !== notificationId));
      });
  }

  reject(commentId: string, notificationId: string) {
    this.storyService
      .rejectComment(`${commentId}`)
      .pipe(
        mergeMap(() => this.crudService.markAsHandled(notificationId)),
        mergeMap(() => this.crudService.markAsRead([notificationId])),
      )
      .subscribe(() => {
        this._notifications.set(this._notifications().filter((item) => item.id !== notificationId));
      });
  }

  approveAll() {
    if (!this.selectedIds().length) return;
    combineLatest(
      this.selectedIds()
        .map((id) => this._notifications().find((notification) => notification.id === id))
        .map((notification) => {
          console.log(notification);
          if (!notification) return of('');
          return this.storyService.approveComment(`${notification?.metadata.commentId}`).pipe(
            mergeMap(() => this.crudService.markAsHandled(notification.id)),
            mergeMap(() => this.crudService.markAsRead([notification.id])),
            map(() => notification.id),
          );
        }),
    )
      .pipe(
        map((ids) => {
          this._notifications.set(this._notifications().filter((item) => !ids.includes(item.id)));
        }),
      )
      .subscribe();
  }

  rejectAll() {
    if (!this.selectedIds().length) return;
    combineLatest(
      this.selectedIds()
        .map((id) => this._notifications().find((notification) => notification.id === id))
        .map((notification) => {
          if (!notification) return of('');
          return this.storyService.rejectComment(`${notification?.metadata.commentId}`).pipe(
            mergeMap(() => this.crudService.markAsHandled(notification.id)),
            mergeMap(() => this.crudService.markAsRead([notification.id])),
            map(() => notification.id),
          );
        }),
    )
      .pipe(
        map((ids) => {
          this._notifications.set(this._notifications().filter((item) => !ids.includes(item.id)));
        }),
      )
      .subscribe();
  }

  private groupByDate = (items: INotification[]) => {
    const groups: Record<string, INotification[]> = {};
    const now = new Date();
    // Удаляем часы, минуты и т.д.
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    items.forEach((item) => {
      const date = new Date(item.date);

      const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      let key: string;

      if (itemDate.getTime() === today.getTime()) {
        key = DayEnum.TODAY;
      } else if (itemDate.getTime() === yesterday.getTime()) {
        key = DayEnum.YESTERDAY;
      } else if (itemDate.getTime() > lastWeek.getTime()) {
        key = DayEnum.LAST_WEEK;
      } else {
        key = DayEnum.LAST_MONTH;
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    // Сортируем по дате убыванию (новее выше)
    const sortedGroups = Object.entries(groups).sort(([aKey], [bKey]) => {
      const aDate = new Date(groups[aKey][0].date);
      const bDate = new Date(groups[bKey][0].date);
      return bDate.getTime() - aDate.getTime(); // ↓ убывание
    });

    return sortedGroups;
  };
}
